import React, { FC, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import HeroSection from "../components/mainSection";
import { FormSection, FormSectionWrapper } from "./style";
import { IconSvg } from "../../../common/constants/images";
import { useNavigate } from "react-router-dom";
import UserService from "../../../services/user.service";
import ModeService from "../../../services/mode.service";

const LoginSignUp: FC = () => {
    const mobileInputRef = useRef<HTMLInputElement | null>(null);
    const [userType, setUserType] = useState<string | null>('');
    const [mobile, setMobile] = useState("");
    const [otp, setOtp] = useState(Array(6).fill(""));
    const [showOtp, setShowOtp] = useState(false);
    const navigate = useNavigate();

    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        const fetchUserType = async () => {
            try {
                const mode = await ModeService.getModeFr();
                setUserType(mode);
                console.log('Fetched userType from getModeFr:', mode);
                console.log('Initial userType:', userType);
            } catch (error) {
                console.error('Error fetching mode:', error);
            }
        };

        fetchUserType();
    }, []);

    useEffect(() => {
        if (userType !== null) {
            console.log('userType updated:', userType);
        }
    }, [userType]);

    const handleMobileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (mobile.length === 10) {
            try {
                await UserService.login(mobile);
                setShowOtp(true);
                setTimeout(() => {
                    otpRefs.current[0]?.focus();
                }, 100);
            } catch (error) {
                alert("Failed to send OTP. Please try again.");
                console.error(error);
            }
        } else {
            alert("Please enter a valid 10-digit mobile number.");
        }
    };

    const handleOtpChange = (value: string, index: number) => {
        if (!/^\d?$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus next
        if (value && index < 5 && otpRefs.current[index + 1]) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const enteredOtp = otp.join("");
    
        if (enteredOtp.length !== 6) {
            alert("Please enter a valid 6-digit OTP");
            return;
        }
    
        try {
            const response = await UserService.verifyOtp(mobile, enteredOtp);
            console.log(response)
            // Optional: Store token in localStorage if needed
            if (response?.status) {
                localStorage.setItem("api_token", response.data.token);
            }
    
            // Navigate based on user type
            if (userType === "employer") {
                navigate("/employer/dashboard");
            } else {
                navigate("/seeker/dashboard");
            }
        } catch (error) {
            alert("OTP verification failed. Please try again.");
            console.error(error);
        }
    };
    

    const backFn = () => {
        setShowOtp(false);
        setOtp(Array(6).fill(""));  // Clear OTP fields
        setTimeout(() => {
            mobileInputRef.current?.focus();
        }, 100);
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === "Backspace") {
            const newOtp = [...otp];
    
            if (otp[index]) {
                // If current box has a digit, just clear it
                newOtp[index] = "";
                setOtp(newOtp);
            } else if (index > 0) {
                // If empty, go to previous and clear that too
                newOtp[index - 1] = "";
                setOtp(newOtp);
                otpRefs.current[index - 1]?.focus();
            }
    
            // Prevent default so the input box doesn't delete again
            e.preventDefault();
        }
    };

    const setMode = (mode: 'seeker' | 'employer') => {
        setUserType(mode);
        ModeService.setMode(mode);
    }

    return (
        <>
            <HeroSection heading="Login" />
            <FormSection>
                <FormSectionWrapper>
                    <Card>

                    <ToggleButtons>
                        {showOtp ? (
                            <>
                                <BackButton type="button" onClick={() => backFn()}>←</BackButton>
                                <ToggleButton active="true" disabled style={{ flex: 1, cursor: "default" }}>
                                    {userType === "seeker" ? "🙋‍♂️ I am a Seeker" : "👔 I am an Employer"}
                                </ToggleButton>
                            </>
                        ) : (
                            <>
                                <ToggleButton active={userType == "seeker" ? 'true' : 'false'} onClick={() => setMode("seeker")}>
                                    🙋‍♂️ I am a Seeker 
                                </ToggleButton>
                                <ToggleButton active={userType == "employer" ? 'true' : 'false'} onClick={() => setMode("employer")}>
                                    👔 I am an Employer
                                </ToggleButton>
                            </>
                        )}
                    </ToggleButtons>

                        <Form onSubmit={showOtp ? handleOtpSubmit : handleMobileSubmit}>
                            {!showOtp ? (
                                <InputWrapper>
                                    <CallingCode>+64</CallingCode>
                                    <Input
                                        ref={mobileInputRef}
                                        type="tel"
                                        name="mobile"
                                        placeholder="Enter Mobile Number"
                                        value={mobile}
                                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                                        maxLength={10}
                                        required
                                    />
                                </InputWrapper>
                            ) : (
                                <>
                                    <ReadonlyMobile>+64 {mobile}</ReadonlyMobile>
                                    <OtpBoxes>
                                        {otp.map((digit, index) => (
                                            <OtpInput
                                                key={index}
                                                type="text"
                                                maxLength={1}
                                                value={digit}
                                                ref={(el) => (otpRefs.current[index] = el)}
                                                onChange={(e) => handleOtpChange(e.target.value, index)}
                                                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                                            />
                                        ))}
                                    </OtpBoxes>
                                </>
                            )}
                            <PrimaryButton type="submit">{showOtp ? "Verify OTP" : "Get OTP"}</PrimaryButton>
                        </Form>
                    </Card>
                </FormSectionWrapper>

                <BgIcon>
                    <IconSvg.ContactFormBg />
                </BgIcon>
            </FormSection>
        </>
    );
};

export default LoginSignUp;

const Card = styled.div`
    background: white;
    padding: 40px 30px;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    max-width: 440px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 15px;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Input = styled.input`
    padding: 14px 18px;
    border: unset;
    border-radius: 10px;
    font-size: 16px;
    transition: border 0.3s, box-shadow 0.3s;

    &:focus {
        outline: none;
    }
`;

const PrimaryButton = styled.button`
    background-color: #007bff;
    color: white;
    padding: 14px;
    border: none;
    font-weight: bold;
    border-radius: 10px;
    font-size: 16px;
    transition: background-color 0.3s;
    cursor: pointer;

    &:hover {
        background-color: #0056b3;
    }
`;

const ToggleButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 15px;
`;

interface ToggleButtonProps {
    active: 'true' | 'false';
}

const ToggleButton = styled.button<ToggleButtonProps>`
    flex: 1;
    padding: 12px 16px;
    font-weight: 600;
    border: 2px solid transparent;
    background-color: ${({ active }) => (active == 'true' ? "#007bff" : "#f0f0f0")};
    color: ${({ active }) => (active == 'true' ? "#fff" : "#333")};
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: ${({ active }) => (active == 'true' ? "#0056b3" : "#e0e0e0")};
        transform: scale(1.03);
    }
`;

const BgIcon = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: -1;
    opacity: 0.2;
`;

const LogoWrapper = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    svg {
        height: 48px;
    }
`;

const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    border: 2px solid #ddd;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;

    &:focus-within {
        border-color: #007bff;
        box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1);
    }
`;

const CallingCode = styled.span`
    padding: 0 14px;
    font-size: 16px;
    color: #333;
`;

const ReadonlyMobile = styled.div`
    text-align: center;
    font-weight: 500;
    font-size: 16px;
    margin-bottom: 10px;
`;

const OtpBoxes = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
`;

const OtpInput = styled.input`
    width: 48px;
    height: 48px;
    text-align: center;
    font-size: 24px;
    border: 2px solid #ccc;
    border-radius: 8px;
    transition: border 0.3s;

    &:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    }
`;

const BackButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;  // Increased font size for visibility
    font-weight: bold; // Make it thicker
    color: #007bff;
    cursor: pointer;
    padding: 0 12px;  // Increased padding for more click area
    align-self: center;
    line-height: 1;

    &:hover {
        color: #0056b3;
    }
`;